import type { FC } from 'react';
import { useDispatch } from 'react-redux';
import APITable from '@/components/UI/Table/Reusable/APITable';
import Header from '@/components/UI/Header';
import Button from '@/components/UI/Button';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import type { UseTableProps, TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model';

import type { FilterOption } from '@/components/UI/Table/TableComponent.model';
import { samplesConfigObj } from '../SampleTypes/SamplesConfig';
import { _notNil, _isNil } from '@/littledash';
import type { SampleType } from '@/model/SampleType.model';
import { ApiService } from '@/support/ApiService.ts';
import { errorToast, successToast } from '@/helpers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';

import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { RouteService } from '@/support/RouteService';

const generateColumns = (): Array<TanStackColumnDef<SampleType>> => [
  {
    id: 'title',
    accessorKey: 'title',
    header: 'Sample type',
    size: 300,
    isVisible: true,
    enableSorting: false,
  },
  {
    id: 'label',
    accessorKey: 'label',
    header: 'Label',
    size: 120,
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: SampleType } }) => (_notNil(row.original.label) ? row.original.label : '-'),
  },
  {
    id: 'details',
    accessorFn: (row: SampleType) => row.options?.details,
    header: 'Details',
    size: 400,
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: SampleType } }) => {
      const details = row.original.options?.details;
      if (_notNil(details) && details.length > 0) {
        return details
          .map((detail) => {
            const config = samplesConfigObj[detail];
            return _notNil(config) ? `${config.title} (${config.units.join(', ')})` : detail;
          })
          .join(', ');
      }
      return 'No details selected';
    },
  },
  {
    id: 'status',
    accessorKey: 'archived_at',
    header: 'Status',
    size: 100,
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: SampleType } }) => {
      const isActive = _isNil(row.original.archived_at);
      return isActive ? 'Active' : 'Archived';
    },
  },
];

const generateFilterOptions = (): Array<FilterOption> => [
  {
    value: 'status',
    name: 'Status',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: [
          { value: 'active', name: 'Active' },
          { value: 'archived', name: 'Archived' },
        ],
      },
    ],
  },
];

const createDefaultActiveFilter = (): TableFilter => ({
  category: {
    name: 'Status',
    value: 'status',
    index: 0,
  },
  operation: {
    name: filterType.eq,
    index: 0,
  },
  option: {
    name: 'Active',
    value: 'active',
  },
});

const SampleGlossaries: FC = () => {
  const dispatch = useDispatch();
  const columns = generateColumns();
  const filterOptions = generateFilterOptions();
  const defaultFilters = [createDefaultActiveFilter()];

  const handleBulkEdit = (sampleTypes: Array<SampleType>, refreshTable: () => void) => {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'BULK_EDIT_SAMPLE_TYPES',
      props: {
        sampleTypes,
        onSave: async () => {
          dispatch({ type: 'CLOSE_MODAL' });
          await refreshTable();
        },
        onCancel: () => {
          dispatch({ type: 'CLOSE_MODAL' });
        },
      },
    });
  };

  const handleArchive = async (sampleTypes: Array<SampleType>, refreshTable: () => void) => {
    const teamGlossaryApiIds = sampleTypes.map((sampleType) => sampleType.api_id);

    try {
      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/archive',
        path: undefined,
        body: { team_glossary_api_ids: teamGlossaryApiIds },
        options: { onError: { toast: false, capture: false, throw: true, slug: 'sample-glossary-archive' } },
      });

      successToast(`Successfully archived ${sampleTypes.length} sample type${sampleTypes.length !== 1 ? 's' : ''}`);
      await refreshTable();
    } catch (error) {
      errorToast('Could not archive sample types');
      ExceptionHandler.captureException(
        new InVivoError('Could not archive sample types', {
          slug: 'sample-glossary-archive',
          cause: error,
        })
      );
    }
  };

  const handleUnarchive = async (sampleTypes: Array<SampleType>, refreshTable: () => void) => {
    const teamGlossaryApiIds = sampleTypes.map((sampleType) => sampleType.api_id);

    try {
      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/unarchive',
        path: undefined,
        body: { team_glossary_api_ids: teamGlossaryApiIds },
        options: { onError: { toast: false, capture: false, throw: true, slug: 'sample-glossary-unarchive' } },
      });

      successToast(`Successfully unarchived ${sampleTypes.length} sample type${sampleTypes.length !== 1 ? 's' : ''}`);
      await refreshTable();
    } catch (error) {
      errorToast('Could not unarchive sample types');
      ExceptionHandler.captureException(
        new InVivoError('Could not unarchive sample types', {
          slug: 'sample-glossary-unarchive',
          cause: error,
        })
      );
    }
  };

  const handleBulkAdd = (refreshTable: () => void) => {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'BULK_ADD_SAMPLE_TYPES',
      props: {
        onSave: async () => {
          dispatch({ type: 'CLOSE_MODAL' });
          await refreshTable();
        },
        onCancel: () => {
          dispatch({ type: 'CLOSE_MODAL' });
        },
      },
    });
  };

  const bulkActions = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: UseTableProps<SampleType>) => {
    const selectedSampleTypes = selectedFlatRows.map(({ original }) => original);

    const archivedCount = selectedSampleTypes.filter((item) => _notNil(item.archived_at)).length;
    const unarchivedCount = selectedSampleTypes.length - archivedCount;
    const hasMixedSelection = archivedCount > 0 && unarchivedCount > 0;
    const allArchived = archivedCount === selectedSampleTypes.length;

    const archiveAction = allArchived ? 'Unarchive' : 'Archive';
    const archiveHandler = allArchived ? handleUnarchive : handleArchive;
    const archiveDisabled = hasMixedSelection;
    const archiveTooltip = hasMixedSelection
      ? 'Cannot archive and unarchive items in the same operation. Please select only archived or only unarchived items.'
      : undefined;

    return [
      {
        name: 'Edit',
        key: 'edit_sample_types',
        action: () => {
          handleBulkEdit(selectedSampleTypes, refetchTableData);
        },
      },
      {
        name: archiveAction,
        key: 'archive_sample_types',
        action: () => {
          if (_isNil(archiveDisabled) || !archiveDisabled) {
            archiveHandler(selectedSampleTypes, refetchTableData);
          }
        },
        disabled: archiveDisabled,
        tooltip: archiveTooltip,
        className: 'red',
      },
    ];
  };

  const AsideComponent = ({ useTableProps }: any) => {
    const {
      apiTable: { fetchTableData },
    } = useTableProps;

    return (
      <Button onClick={() => handleBulkAdd(fetchTableData)} testId="create-sample-type-button">
        Add new
      </Button>
    );
  };

  return (
    <div>
      <Header mainHeaderText="Samples" className="pb2" />
      <APITable
        columns={columns}
        filterOptions={filterOptions}
        apiInfo={{
          type: 'internalApi',
          route: RouteService.api({
            endpoint: 'GET /api/v1/team-glossary',
            path: undefined,
            query: {
              group: 'samples',
              sort: 'title',
            },
          }).url,
        }}
        queryParams={{
          group: 'samples',
          sort: 'title',
        }}
        defaultSortBy={{ id: 'title', desc: false }}
        defaultFilters={defaultFilters}
        searchPlaceholderText="Search sample types..."
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={25}
        testIdPrefix="samples-v2"
        loadingText="Loading sample types..."
        noDataMessage="No sample types found"
        bulkActions={bulkActions}
        AsideComponent={AsideComponent}
      />
    </div>
  );
};

export default SampleGlossaries;
